/**
 * Keeps asset lineage presentation consistent across asset views while attaching
 * advisory release-date context directly to the source customers already inspect.
 */
import React from 'react';
import Tag from '@amzn/meridian/tag';
import type { TagType } from '@amzn/meridian/tag/tag';
import Tooltip from '@amzn/meridian/tooltip';
import { useTranslation } from 'src/arb-wrapper';
import { FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import type { AssetSourceLabel } from 'src/utils/assetSourceLabel';
import { deriveAssetSourceLabel } from 'src/utils/assetSourceLabel';
import { deriveMarketingCaution } from 'src/utils/marketingCaution';

const ASSET_SOURCE_LABEL_KEYS: Record<AssetSourceLabel, string> = {
  AUTOGEN: 'asset_source_autogen',
  BOX: 'asset_source_box',
  VAULT: 'asset_source_vault',
};

const ASSET_SOURCE_TAG_TYPES: Record<AssetSourceLabel, TagType> = {
  AUTOGEN: 'theme',
  BOX: 'success',
  VAULT: 'neutral',
};

interface AssetSourceTagProps {
  readonly assetId?: string;
  readonly generationMethods?: string[];
  readonly releaseDates?: ReadonlyArray<string | undefined>;
}

export const AssetSourceTag: React.FC<AssetSourceTagProps> = ({
  assetId,
  generationMethods,
  releaseDates,
}) => {
  const { t } = useTranslation();

  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS)) return null;

  const sourceLabel = deriveAssetSourceLabel(generationMethods);

  if (sourceLabel === null) return null;

  const tag = (
    <Tag
      data-testid="asset-source-tag"
      type={ASSET_SOURCE_TAG_TYPES[sourceLabel]}
    >
      {t(ASSET_SOURCE_LABEL_KEYS[sourceLabel])}
    </Tag>
  );

  if (!deriveMarketingCaution(generationMethods, releaseDates)) return tag;

  return (
    <Tooltip
      id={assetId === undefined
        ? undefined
        : `asset-marketing-caution-tooltip-${assetId}`}
      position="end"
      title={t('asset_marketing_caution_tooltip')}
    >
      {tag}
    </Tooltip>
  );
};
